import { useState, type ReactNode } from "react";
import { darfJetzt, useUserContext } from "@/auth/operations";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plug, Pencil, Trash2, RefreshCw, LinkIcon, Unplug } from "lucide-react";
import { useConnector, useConnectorAgents } from "@/api/queries/connectors";
import { useDeleteConnector, useConnectApiKey, useConnectLogin, useConnectClientCredentials, useConnectEnv, useStartOAuth, useDisconnectConnector, useSetOAuthClientSecret, useClearOAuthClientSecret } from "@/api/mutations/connectors";
import { SectionCard } from "@/components/SectionCard";
import { envFelder, spec2, unavailableText } from "@/types";
import { PropGrid } from "@/components/PropGrid";
import { AccessRequestsSection } from "@/components/connector-access/AccessRequestsSection";
import { BackLink, DetailHeader } from "@/components/DetailHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UpdateConnectorDialog } from "@/components/connector-actions/UpdateConnectorDialog";
import { SetConnectorOwnerDialog } from "@/components/connector-actions/SetConnectorOwnerDialog";
import { OwnerValue } from "@/components/ownership/SetOwnerDialog";

export function ConnectorDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, refetch } = useConnector(id);
  const agents = useConnectorAgents(id);
  const del = useDeleteConnector();
  const connectApiKey = useConnectApiKey();
  const connectLogin = useConnectLogin();
  const connectCc = useConnectClientCredentials();
  const connectEnv = useConnectEnv();
  const startOAuth = useStartOAuth();
  const disconnect = useDisconnectConnector();
  const setClientSecret = useSetOAuthClientSecret();
  const clearClientSecret = useClearOAuthClientSecret();
  const navigate = useNavigate();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [ownerDialog, setOwnerDialog] = useState(false);
  // E-3: nur ein Admin darf ownerlos machen. Die Oberflaeche bietet es sonst gar nicht an —
  // der Server lehnt es ohnehin ab (OWNER_CLEAR_FORBIDDEN), aber ein Knopf, der immer
  // scheitert, ist schlechter als keiner.
  // #299: "ownerlos machen" ist eine Plattform-Entscheidung (#273) — der Server sagt,
  // wer sie treffen darf, statt dass die UI die Rolle raet.
  // R2 L-2/L-E: Vorgang statt Rollen-String; waehrend des Ladens nicht anbieten, bei
  // fehlgeschlagenem Kontext-Abruf wieder offen (der Server lehnt notfalls ab).
  const darfOwnerEntfernen = darfJetzt(useUserContext(), "connector.manage");
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [clientSecretValue, setClientSecretValue] = useState("");
  // #336 (UC-1): Client-Credentials (Service-Identität) — beides write-only → Vault.
  const [ccClientId, setCcClientId] = useState("");
  const [ccClientSecret, setCcClientSecret] = useState("");
  // unified-managed-mcp: ENV-Werte für lokale Connectors (name→value, write-only).
  const [envValues, setEnvValues] = useState<Record<string, string>>({});

  if (isLoading) return <SectionCard testId="detail-loading"><p className="text-text3">Lädt …</p></SectionCard>;
  if (isError || !data) {
    return (
      <SectionCard testId="detail-error">
        <BackLink to="/connectors" label="Connectors" />
        <p className="mt-3 text-text3">Connector nicht gefunden.</p>
      </SectionCard>
    );
  }

  const connected = data.connection?.connected ?? false;
  // #253 (UC-A): kind-spezifische Auth-Config (narrowed) für die Anzeige/Connect-Formulare.
  const oauthCfg = data.auth.kind === "oauth" ? data.auth : null;
  // #297: die EINE Beschreibung vom Master. Unbekanntes `method` (neuerer Master) faellt
  // durch alle Zweige = nichts anbieten — default-closed statt geratenem Formular.
  // #297 (Review HIGH-1): NIE roh dereferenzieren — eine Antwort ohne connectSpec
  // (aelterer Master, getrennt deployte UI) haette die ganze Seite leer gerendert.
  const spec = spec2(data.connectSpec);
  // Anzeige der Login-Konfiguration (nicht die Verbinden-Entscheidung — die trifft spec).
  const loginCfg = data.auth.kind === "login" ? data.auth : null;
  // #336 (UC-1): Anzeige der Client-Credentials-Konfiguration (die Creds selbst nie).
  const ccCfg = data.auth.kind === "client-credentials" ? data.auth : null;

  function doConnectLogin() {
    if (!loginUser.trim() || !loginPass || !data) return;
    connectLogin.mutate(
      { id: data.id, username: loginUser.trim(), password: loginPass },
      {
        onSuccess: () => { setLoginUser(""); setLoginPass(""); toast.success("Verbunden"); },
        onError: (err) => toast.error(`Verbinden fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  // #336 (UC-1): Client-ID + Client-Secret write-only → Vault; den Token holt der Master.
  function doConnectClientCredentials() {
    if (!ccClientId.trim() || !ccClientSecret.trim() || !data) return;
    connectCc.mutate(
      { id: data.id, clientId: ccClientId.trim(), clientSecret: ccClientSecret.trim() },
      {
        onSuccess: () => { setCcClientId(""); setCcClientSecret(""); toast.success("Verbunden"); },
        onError: (err) => toast.error(`Verbinden fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function doConnectApiKey() {
    if (!apiKeyValue.trim() || !data) return;
    connectApiKey.mutate(
      { id: data.id, value: apiKeyValue.trim() },
      {
        onSuccess: () => { setApiKeyValue(""); toast.success("Verbunden"); },
        onError: (err) => toast.error(`Verbinden fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function doConnectEnv() {
    if (!data) return;
    const vars: Record<string, string> = {};
    for (const [k, v] of Object.entries(envValues)) {
      if (k.trim() && v !== "") vars[k.trim()] = v;
    }
    // ALLE Pflichtfelder erzwingen (siehe ConnectAgentConnectorDialog): die Delivery
    // laesst den Connector ohne binding-ENV/required extraEnv aus, und `connectEnv`
    // ERSETZT das Buendel — ein Teil-Update wuerde die uebrigen Variablen loeschen.
    const fehlend = envFelder(data.connectSpec).filter((f) => f.required && !vars[f.name]).map((f) => f.name);
    if (fehlend.length > 0) { toast.error(`Pflichtfelder fehlen: ${fehlend.join(", ")}`); return; }
    if (Object.keys(vars).length === 0) { toast.error("Mindestens eine Variable mit Wert angeben"); return; }
    connectEnv.mutate(
      { id: data.id, vars },
      {
        onSuccess: () => { setEnvValues({}); toast.success("Verbunden (ENV gespeichert)"); },
        onError: (err) => toast.error(`Verbinden fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function doStartOAuth() {
    if (!data) return;
    startOAuth.mutate(data.id, {
      onSuccess: ({ authorizeUrl }) => {
        window.open(authorizeUrl, "_blank", "noopener");
        toast.message("OAuth-Login im neuen Tab geöffnet — nach Freigabe hier 'Status aktualisieren'.");
      },
      onError: (err) => toast.error(`OAuth-Start fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function doSetClientSecret() {
    if (!clientSecretValue.trim() || !data) return;
    setClientSecret.mutate(
      { id: data.id, value: clientSecretValue.trim() },
      {
        onSuccess: () => { setClientSecretValue(""); toast.success("Client-Secret gespeichert"); },
        onError: (err) => toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function doClearClientSecret() {
    if (!data) return;
    clearClientSecret.mutate(data.id, {
      onSuccess: () => toast.success("Client-Secret entfernt"),
      onError: (err) => toast.error(`Entfernen fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function doDisconnect() {
    if (!data) return;
    disconnect.mutate(data.id, {
      onSuccess: () => toast.success("Verbindung getrennt"),
      onError: (err) => toast.error(`Trennen fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <DetailHeader
        backTo="/connectors"
        backLabel="Connectors"
        icon={Plug}
        title={data.name}
        badge={<Badge variant={data.auth.kind === "none" ? "default" : "enrolled"}>{data.auth.kind}</Badge>}
        sub={data.description || data.url}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}><Pencil className="h-4 w-4" /> Bearbeiten</Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}><Trash2 className="h-4 w-4" /> Löschen</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          {/* Verbindung */}
          <SectionCard
            icon={<LinkIcon className="h-[18px] w-[18px]" />}
            title="Verbindung"
            description="Auth-Material liegt verschlüsselt im Vault — nie im Klartext sichtbar."
            action={
              <Button variant="ghost" size="sm" onClick={() => refetch()} title="Status aktualisieren">
                <RefreshCw className="h-4 w-4" />
              </Button>
            }
          >
            <div className="space-y-3">
              <div className="text-[13px]">
                Status:{" "}
                {connected ? (
                  <span className="text-ok-text">● verbunden{data.connection?.expiresAt ? ` (Token läuft ab: ${new Date(data.connection.expiresAt).toLocaleString()})` : ""}</span>
                ) : (
                  <span className="text-text3">○ nicht verbunden</span>
                )}
              </div>

              {/* #297: EIN Zweig statt transport×auth-Fallunterscheidung. Der Master
                  beschreibt in `connectSpec`, WIE verbunden wird; hier wird nur noch
                  gerendert. Vorher stand die Ableitung hier UND im Per-Agent-Dialog UND
                  in der User-App — drei Kopien, die auseinanderliefen. */}
              {spec.method === "env" && (
                <div className="space-y-2">
                  <div className="space-y-2 rounded-md border border-border bg-surface2 p-3" data-testid="conn-env-form">
                    <p className="text-[12px] text-text3">
                      Werte sind write-only (→ Vault) und werden am Agent vorbei injiziert.
                    </p>
                    {envFelder(spec).map((feld) => (
                      <div key={feld.name} className="space-y-1">
                        <label className="block text-[12px] font-semibold text-text2">
                          {feld.label || feld.name} {feld.required && <span className="text-danger">*</span>}{" "}
                          <span className="mono text-[11px] text-text3">{feld.name}{feld.secret ? " · secret" : ""}</span>
                        </label>
                        <Input
                          type={feld.secret ? "password" : "text"}
                          value={envValues[feld.name] ?? ""}
                          onChange={(e) => setEnvValues((v) => ({ ...v, [feld.name]: e.target.value }))}
                          placeholder={feld.placeholder || ""}
                          className="mono text-[12px]"
                        />
                      </div>
                    ))}
                    <Button size="sm" onClick={doConnectEnv} disabled={connectEnv.isPending} data-testid="conn-env-connect">
                      {connectEnv.isPending ? "…" : connected ? "Werte aktualisieren" : "Verbinden"}
                    </Button>
                  </div>
                  {connected && (
                    <Button variant="outline" size="sm" onClick={doDisconnect} disabled={disconnect.isPending}>
                      <Unplug className="h-4 w-4" /> Verbindung trennen
                    </Button>
                  )}
                </div>
              )}

              {spec.method === "none" && (
                <p className="text-[12.5px] text-text3" data-testid="conn-none-hint">
                  Kein Credential nötig — direkt für Agents nutzbar.
                </p>
              )}

              {/* #297: ein bewusst nicht bedienbarer Fall wird BENANNT, nicht versteckt —
                  sonst sucht der Admin ein Formular, das nie wirken könnte. */}
              {spec.method === "unavailable" && (
                <p className="text-[12.5px] text-text3" data-testid="conn-unavailable-hint">
                  {unavailableText(spec.reason)}
                </p>
              )}

              {spec.method === "api-key" && !connected && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="mb-1 block text-[12px] font-semibold text-text2">API-Key / Token (write-only)</label>
                    <Input type="password" value={apiKeyValue} onChange={(e) => setApiKeyValue(e.target.value)} placeholder="sk-…" className="mono text-[12px]" data-testid="conn-apikey-value" />
                  </div>
                  <Button size="sm" onClick={doConnectApiKey} disabled={!apiKeyValue.trim() || connectApiKey.isPending}>Verbinden</Button>
                </div>
              )}

              {/* #253 (UC-A): Login-Endpoint — user/pw write-only; Token holt der Master. */}
              {spec.method === "login" && !connected && (
                <div className="space-y-2 rounded-md border border-border bg-surface2 p-3" data-testid="conn-login-form">
                  <p className="text-[11.5px] text-text3">Login-Credentials (write-only → Vault). Der Master holt den Token am {loginCfg?.tokenUrl} + refresht ihn; das LLM sieht weder Passwort noch Token.</p>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="mb-1 block text-[12px] font-semibold text-text2">Benutzername</label>
                      <Input value={loginUser} onChange={(e) => setLoginUser(e.target.value)} placeholder="user" className="mono text-[12px]" data-testid="conn-login-user" />
                    </div>
                    <div className="flex-1">
                      <label className="mb-1 block text-[12px] font-semibold text-text2">Passwort</label>
                      <Input type="password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} placeholder="••••••" className="mono text-[12px]" data-testid="conn-login-pass" />
                    </div>
                    <Button size="sm" onClick={doConnectLogin} disabled={!loginUser.trim() || !loginPass || connectLogin.isPending}>Verbinden</Button>
                  </div>
                </div>
              )}

              {/* #336 (UC-1): Service-Identität — zwei Secret-Felder, kein Browser-Flow. */}
              {spec.method === "client-credentials" && !connected && (
                <div className="space-y-2 rounded-md border border-border bg-surface2 p-3" data-testid="conn-cc-form">
                  <p className="text-[11.5px] text-text3">
                    Client-ID + Client-Secret (write-only → Vault). Der Master holt den Token selbst am {ccCfg?.tokenUrl} und erneuert ihn bei Ablauf; das LLM sieht ihn nie.
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="mb-1 block text-[12px] font-semibold text-text2">Client-ID</label>
                      <Input value={ccClientId} onChange={(e) => setCcClientId(e.target.value)} placeholder="service-user" className="mono text-[12px]" data-testid="conn-cc-clientid" />
                    </div>
                    <div className="flex-1">
                      <label className="mb-1 block text-[12px] font-semibold text-text2">Client-Secret</label>
                      <Input type="password" value={ccClientSecret} onChange={(e) => setCcClientSecret(e.target.value)} placeholder="••••••" className="mono text-[12px]" data-testid="conn-cc-clientsecret" />
                    </div>
                    <Button size="sm" onClick={doConnectClientCredentials} disabled={!ccClientId.trim() || !ccClientSecret.trim() || connectCc.isPending} data-testid="conn-cc-connect">Verbinden</Button>
                  </div>
                </div>
              )}

              {data.auth.kind === "oauth" && oauthCfg?.clientId && (
                <div className="space-y-2 rounded-md border border-border bg-surface2 p-3">
                  <div className="text-[12.5px] font-semibold text-text2">
                    Client-Secret (vertraulicher Client)
                    {data.connection?.oauthClientSecretSet
                      ? <span className="ml-2 text-[11.5px] text-ok-text">● hinterlegt</span>
                      : <span className="ml-2 text-[11.5px] text-text3">○ nicht hinterlegt</span>}
                  </div>
                  <p className="text-[11.5px] text-text3">
                    Nur für Provider, die ein App-Secret verlangen (z.B. Asana, Entra). Public Clients / DCR brauchen keins. Write-only — landet verschlüsselt im Vault.
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input type="password" value={clientSecretValue} onChange={(e) => setClientSecretValue(e.target.value)} placeholder="Client-Secret eintragen / ersetzen" className="mono text-[12px]" data-testid="conn-clientsecret-value" />
                    </div>
                    <Button size="sm" onClick={doSetClientSecret} disabled={!clientSecretValue.trim() || setClientSecret.isPending} data-testid="conn-clientsecret-save">Speichern</Button>
                    {data.connection?.oauthClientSecretSet && (
                      <Button variant="ghost" size="sm" onClick={doClearClientSecret} disabled={clearClientSecret.isPending} title="Client-Secret entfernen">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {data.auth.kind === "oauth" && !oauthCfg?.clientId && (
                <p className="text-[12px] text-text3" data-testid="conn-confidential-hint">
                  DCR / Public-Client — kein App-Secret nötig. Für einen <strong>vertraulichen Client</strong> (z.B. Asana, Entra): Client-ID via „Bearbeiten" setzen → dann erscheint hier das Client-Secret-Feld.
                </p>
              )}

              {spec.method === "oauth" && !connected && (
                <Button size="sm" onClick={doStartOAuth} disabled={startOAuth.isPending} data-testid="conn-oauth-start">
                  <LinkIcon className="h-4 w-4" /> {startOAuth.isPending ? "Starte …" : "OAuth verbinden"}
                </Button>
              )}

              {spec.method !== "none" && spec.method !== "env" && connected && (
                <Button variant="outline" size="sm" onClick={doDisconnect} disabled={disconnect.isPending}>
                  <Unplug className="h-4 w-4" /> Verbindung trennen
                </Button>
              )}
            </div>
          </SectionCard>

          {/* Genutzt von */}
          <SectionCard icon={<Plug className="h-[18px] w-[18px]" />} title="Genutzt von" description="Agents, die diesen Connector aktiviert haben">
            {agents.isLoading ? (
              <p className="text-[13px] text-text3">Lade …</p>
            ) : (agents.data?.length ?? 0) === 0 ? (
              <p className="text-[13px] text-text3">Von keinem Agent aktiviert.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="conn-used-by">
                {(agents.data ?? []).map((a) => (
                  <li key={a.agentId} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5">
                    <Link to={`/agents/${a.agentId}`} className="text-[13px] font-medium text-accent-text hover:underline">{a.agentId}</Link>
                    <Badge variant={a.enabled ? "active" : "disabled"}>{a.enabled ? "enabled" : "disabled"}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* #279: Zugriffs-Anfragen auf diesen Connector (Freigeben/Ablehnen/Entziehen). */}
          <AccessRequestsSection connectorId={data.id} />
        </div>

        <PropGrid
          rows={[
            ["ID", <span className="mono text-[12px]">{data.id}</span>],
            // #279: ohne Fachverantwortlichen gehoert der Connector dem System — dann
            // aktivieren Agent-Owner ihn selbst (sofern die Firma nicht "alles beantragen" aktiviert hat).
            [
              "Verantwortlich",
              <span className="flex items-center gap-2">
                {/* #292 (Review LOW-12): DIESELBE Owner-Anzeige wie bei den Artefakten
                    (Email verlinkt / System / orphaned) — der #279-Übergabe-DIALOG bleibt
                    eigenständig, nur die Darstellung ist geteilt. */}
                <OwnerValue ownerId={data.ownerId} />
                {/* #279/E-3: erst ein gesetzter Verantwortlicher erzeugt die Antragspflicht —
                    ohne diesen Knopf war dieser Zustand in der Oberflaeche nicht herstellbar. */}
                <button
                  className="text-[12px] text-accent hover:underline"
                  onClick={() => setOwnerDialog(true)}
                  data-testid="owner-edit"
                >
                  {data.ownerId ? "ändern" : "festlegen"}
                </button>
              </span>,
            ],
            // #237: das rohe `type` ist immer "remote-mcp" (Phase-1-Achse) — fuer den Nutzer
            // irrefuehrend bei local. Transport-abgeleitetes Label zeigen.
            ["Typ", data.transport === "local" ? "Lokal (stdio)" : data.transport === "cli" ? "Managed CLI-Tool" : data.transport === "openapi" ? "REST-API (OpenAPI)" : "Remote-MCP"],
            ["Transport", data.transport],
            ...(data.transport === "local"
              ? ([
                  ["Command", <span className="mono text-[12px] break-all">{data.local?.command}</span>],
                  ["Args", <span className="mono text-[12px] break-all">{data.local?.args.join(" ") || "—"}</span>],
                ] as Array<[string, ReactNode]>)
              : data.transport === "cli"
              ? ([
                  ["bin", <span className="mono text-[12px] break-all">{data.cli?.bin}</span>],
                  ["Install", <span className="mono text-[12px] break-all">{data.cli?.installCmd || "— (self-installed)"}</span>],
                  ["Auth-Shape", data.cli?.authShape ?? "—"],
                  ["Binding", <span className="mono text-[12px] break-all">{data.cli?.binding || "—"}</span>],
                ] as Array<[string, ReactNode]>)
              : data.transport === "openapi"
              ? ([
                  ["Basis-URL", <span className="mono text-[12px] break-all">{data.openapi?.baseUrl}</span>],
                  ["Spec-URL", <span className="mono text-[12px] break-all">{data.openapi?.specUrl}</span>],
                  ["Tool-Modus", data.openapi?.mode ?? "—"],
                  ["Tags", (data.openapi?.includeTags.length ? data.openapi.includeTags.join(" ") : "— (alle)")],
                  ["Auth", data.auth.kind],
                ] as Array<[string, ReactNode]>)
              : ([
                  ["URL", <span className="mono text-[12px] break-all">{data.url}</span>],
                  ["Auth", data.auth.kind],
                ] as Array<[string, ReactNode]>)),
            ...(data.auth.kind === "oauth"
              ? ([
                  ["OAuth Client-ID", data.auth.clientId ?? "— (DCR)"],
                  ["OAuth Scopes", data.auth.scopes.length ? data.auth.scopes.join(" ") : "—"],
                  ["Auth-Server-Hint", data.auth.authServerHint ?? "—"],
                  // #336 (UC-2): gesetzte Endpoints heissen „keine Discovery" — das gehört sichtbar.
                  ["Authorize-Endpoint", <span className="mono text-[12px] break-all">{data.auth.authorizationEndpoint ?? "— (Discovery)"}</span>],
                  ["Token-Endpoint", <span className="mono text-[12px] break-all">{data.auth.tokenEndpoint ?? "— (Discovery)"}</span>],
                ] as Array<[string, ReactNode]>)
              : []),
            ...(data.auth.kind === "static"
              ? ([["Header", `${data.auth.scheme} (${data.auth.header})`]] as Array<[string, ReactNode]>)
              : []),
            ...(data.auth.kind === "client-credentials"
              ? ([
                  ["Token-URL", <span className="mono text-[12px] break-all">{ccCfg?.tokenUrl}</span>],
                  ["Scopes", data.auth.scopes.length ? data.auth.scopes.join(" ") : "—"],
                  ["Client-Auth", data.auth.authStyle],
                ] as Array<[string, ReactNode]>)
              : []),
            ...(data.auth.kind === "login"
              ? ([
                  ["Login-URL", <span className="mono text-[12px] break-all">{loginCfg?.tokenUrl}</span>],
                  ["Token-Pfad", <span className="mono text-[12px]">{data.auth.tokenPath}</span>],
                ] as Array<[string, ReactNode]>)
              : []),
            ["Erstellt", new Date(data.createdAt).toLocaleString()],
          ]}
        />
      </div>

      {editOpen && <UpdateConnectorDialog connector={data} open onOpenChange={setEditOpen} />}
      {/* Nur mit offenem Dialog rendern — sonst laeuft die Users-Query im Hintergrund. */}
      {ownerDialog && (
        <SetConnectorOwnerDialog
          connectorId={data.id}
          currentOwnerId={data.ownerId ?? null}
          // Ownerlos machen darf nur ein Admin (E-3) — sonst gar nicht erst anbieten.
          canClear={darfOwnerEntfernen}
          open
          onOpenChange={setOwnerDialog}
        />
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Connector löschen"
        description={`"${data.name}" wird gelöscht — für alle Agents weg, Vault-Verbindung entfernt.`}
        confirmText="Löschen"
        confirmVariant="destructive"
        requiredTypeText={data.name}
        isPending={del.isPending}
        onConfirm={() =>
          del.mutate(data.id, {
            onSuccess: () => { toast.success("Connector gelöscht"); navigate("/connectors"); },
            onError: (err) => toast.error(`Löschen fehlgeschlagen: ${(err as Error).message}`),
          })
        }
      />
    </div>
  );
}
